import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, FlaskConical, Hospital, Loader2, Phone, Search, Stethoscope, UserPlus, UserRound, X,
  type LucideIcon,
} from 'lucide-react';
import { APPOINTMENT_STATUS_LABEL, EMPLOYMENT_STATUS_LABEL, EXAM_STATUS_LABEL } from '@dru/types';
import { Dialog, ErrorState, TextSkeleton, cn } from '@dru/ui';
import { api } from '../lib/api';
import { errorMessage } from '../lib/auth';

interface Employee { id: string; fullName: string; employeeCode: string; mobile: string | null; status: string; tenant: { id: string; name: string } }
interface Results {
  employees: Employee[];
  companies: Array<{ id: string; name: string; city: string | null; status: string; contactName: string | null }>;
  leads: Array<{ id: string; organisationName: string; contactName: string | null; status: string; city: string | null }>;
  labs: Array<{ id: string; name: string; code: string; city: string | null; type: string; status: string }>;
  physicians: Array<{ id: string; fullName: string; registrationNo: string; city: string | null; status: string }>;
}
interface Hit { key: string; icon: LucideIcon; title: string; sub: string; tag?: string; employee?: Employee; to?: string }

const nice = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

/**
 * The header search for platform staff (owner, 30 Oct 2026): a name, mobile,
 * code or email finds the employee, company, lead, lab or doctor. An employee
 * opens in place, with their cases; the rest open their own page.
 */
export function PlatformSearch({ className, prefixes, autoFocus, onNavigate }: {
  className?: string;
  /** Consoles this login can open; a result elsewhere is listed but not linked. */
  prefixes: string[];
  autoFocus?: boolean;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [res, setRes] = useState<Results | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [peek, setPeek] = useState<Employee | null>(null);

  const can = (p: string) => prefixes.includes(p);
  const lead = can('/admin') ? '/admin/leads' : can('/ops') ? '/ops/leads' : null;

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); setError(null); return; }
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get<Results>(`/platform/search?q=${encodeURIComponent(term)}`);
        if (live) { setRes(r); setError(null); setActive(0); }
      } catch (e) {
        if (live) { setRes(null); setError(errorMessage(e)); }
      } finally {
        if (live) setBusy(false);
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const groups = useMemo(() => {
    if (!res) return [];
    const g: Array<{ title: string; hits: Hit[] }> = [
      { title: 'Employees', hits: res.employees.map((e) => ({
        key: `e${e.id}`, icon: UserRound, title: e.fullName, employee: e,
        sub: [e.employeeCode, e.mobile, e.tenant.name].filter(Boolean).join(' · '),
        tag: EMPLOYMENT_STATUS_LABEL[e.status as keyof typeof EMPLOYMENT_STATUS_LABEL] ?? nice(e.status),
      })) },
      { title: 'Companies', hits: res.companies.map((c) => ({
        key: `c${c.id}`, icon: Building2, title: c.name, tag: nice(c.status),
        sub: [c.contactName, c.city].filter(Boolean).join(' · ') || 'Company',
        to: can('/admin') ? `/admin/companies/${c.id}` : undefined,
      })) },
      { title: 'Leads', hits: res.leads.map((l) => ({
        key: `l${l.id}`, icon: UserPlus, title: l.organisationName, tag: nice(l.status),
        sub: [l.contactName, l.city].filter(Boolean).join(' · ') || 'Lead',
        to: lead ? `${lead}/${l.id}` : undefined,
      })) },
      { title: 'Labs and hospitals', hits: res.labs.map((p) => ({
        key: `p${p.id}`, icon: p.type === 'LAB' ? FlaskConical : Hospital, title: p.name, tag: nice(p.status),
        sub: [p.code, p.city].filter(Boolean).join(' · '),
        to: can('/admin') ? `/admin/partners/${p.id}` : undefined,
      })) },
      { title: 'Doctors', hits: res.physicians.map((d) => ({
        key: `d${d.id}`, icon: Stethoscope, title: d.fullName, tag: nice(d.status),
        sub: [d.registrationNo, d.city].filter(Boolean).join(' · '),
        to: can('/admin') ? `/admin/physicians/${d.id}` : undefined,
      })) },
    ];
    return g.filter((x) => x.hits.length);
  }, [res, prefixes]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = groups.flatMap((g) => g.hits);

  const pick = (h: Hit) => {
    setOpen(false);
    input.current?.blur();
    if (h.employee) { setPeek(h.employee); return; }
    if (h.to) { setQ(''); setRes(null); navigate(h.to); onNavigate?.(); }
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); pick(flat[active] ?? flat[0]!); }
  };

  const showList = open && q.trim().length >= 2;
  let n = -1;

  return (
    <div ref={box} className={cn('relative', className)}>
      <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-label="Search employees, companies, leads, labs and doctors"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && flat.length ? `${listId}-${active}` : undefined}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search name, mobile, code…"
        autoComplete="off"
        autoFocus={autoFocus}
        className="h-10 w-full rounded-btn border border-neutral-200 bg-neutral-50 pl-9 pr-9 text-body text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 [&::-webkit-search-cancel-button]:hidden"
      />
      {busy ? (
        <Loader2 aria-hidden className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-neutral-400" />
      ) : q ? (
        <button type="button" aria-label="Clear search" onClick={() => { setQ(''); input.current?.focus(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-400 hover:text-neutral-700">
          <X className="h-4 w-4" />
        </button>
      ) : null}

      {showList && (
        <div id={listId} role="listbox"
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-[28rem] overflow-auto rounded-card border border-neutral-200 bg-white py-1 shadow-lg sm:min-w-[26rem]">
          {error && <p className="px-3 py-2 text-caption text-danger-600">{error}</p>}
          {!error && res && flat.length === 0 && !busy && (
            <p className="px-3 py-3 text-caption text-neutral-500">Nothing matches “{q.trim()}”.</p>
          )}
          {!res && busy && <p className="px-3 py-2 text-caption text-neutral-500">Searching…</p>}
          {groups.map((g) => (
            <div key={g.title} role="group" aria-label={g.title}>
              <p className="px-3 pb-1 pt-2 text-micro font-semibold uppercase tracking-wide text-neutral-500">{g.title}</p>
              {g.hits.map((h) => {
                n += 1;
                const i = n;
                const dead = !h.employee && !h.to;
                return (
                  <button
                    key={h.key} id={`${listId}-${i}`} role="option" aria-selected={i === active} type="button"
                    aria-disabled={dead || undefined}
                    onMouseEnter={() => setActive(i)} onClick={() => pick(h)}
                    className={cn('flex w-full items-center gap-3 px-3 py-2 text-left', i === active ? 'bg-primary-50' : 'hover:bg-neutral-50', dead && 'cursor-default opacity-70')}
                  >
                    <span aria-hidden className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-600">
                      <h.icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body font-medium text-neutral-900">{h.title}</span>
                      <span className="block truncate text-caption text-neutral-500">{h.sub}</span>
                    </span>
                    {h.tag && <span className="shrink-0 text-caption text-neutral-500">{h.tag}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {peek && (
        <EmployeePeek
          employee={peek}
          canOpenCase={can('/ops')}
          onClose={() => setPeek(null)}
          onOpen={(to) => { setPeek(null); setQ(''); setRes(null); navigate(to); onNavigate?.(); }}
        />
      )}
    </div>
  );
}

interface SupportCase {
  id: string;
  company: { id: string; name: string };
  plant: { id: string; name: string } | null;
  employee: { employeeCode: string; fullName: string; mobile: string | null };
  status: string;
  lab: { name: string | null } | null;
  location: { name: string; city: string | null } | null;
  appointment: { scheduledFor: string; status: string; location: { name: string; city: string | null } | null } | null;
  notifications: Array<{ eventType: string; channel: string; status: string; lastError: string | null; createdAt: string }>;
}

const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/** One employee, in place: who they are and where each of their checkups stands. */
function EmployeePeek({ employee, canOpenCase, onClose, onOpen }: {
  employee: Employee; canOpenCase: boolean; onClose: () => void; onOpen: (to: string) => void;
}) {
  const [cases, setCases] = useState<SupportCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setCases(null); setError(null);
    api.get<{ items: SupportCase[] }>(`/platform/support/cases?tenantId=${employee.tenant.id}&search=${encodeURIComponent(employee.employeeCode)}&limit=20`)
      .then((r) => { if (live) setCases(r.items.filter((c) => c.employee.employeeCode === employee.employeeCode)); })
      .catch((e) => { if (live) setError(errorMessage(e)); });
    return () => { live = false; };
  }, [employee, tick]);

  return (
    <Dialog title={employee.fullName} onClose={onClose} wide>
      <div className="space-y-4">
        <dl className="grid grid-cols-2 gap-3 rounded-card border border-neutral-200 bg-neutral-50/60 p-3 sm:grid-cols-4">
          <div><dt className="text-caption text-neutral-500">Employee code</dt><dd className="font-mono text-small text-neutral-900">{employee.employeeCode}</dd></div>
          <div>
            <dt className="text-caption text-neutral-500">Mobile</dt>
            <dd className="text-small text-neutral-900">
              {employee.mobile ? <a href={`tel:${employee.mobile}`} className="inline-flex items-center gap-1 text-primary-700 hover:underline"><Phone className="h-3.5 w-3.5" aria-hidden />{employee.mobile}</a> : '—'}
            </dd>
          </div>
          <div><dt className="text-caption text-neutral-500">Company</dt><dd className="truncate text-small text-neutral-900">{employee.tenant.name}</dd></div>
          <div><dt className="text-caption text-neutral-500">Status</dt><dd className="text-small text-neutral-900">{EMPLOYMENT_STATUS_LABEL[employee.status as keyof typeof EMPLOYMENT_STATUS_LABEL] ?? nice(employee.status)}</dd></div>
        </dl>

        <section aria-label="Checkups">
          <h3 className="mb-2 text-small font-semibold text-neutral-900">Checkups</h3>
          {error ? <ErrorState title="Could not load the checkups" detail={error} onRetry={() => setTick((t) => t + 1)} />
            : !cases ? <TextSkeleton lines={4} />
            : cases.length === 0 ? (
              <p className="rounded-card border border-dashed border-neutral-200 px-3 py-6 text-center text-small text-neutral-500">No open checkup for this employee.</p>
            ) : (
              <ul className="space-y-2">
                {cases.map((c) => (
                  <li key={c.id} className="rounded-card border border-neutral-200 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-small font-medium text-neutral-900">
                          {EXAM_STATUS_LABEL[c.status as keyof typeof EXAM_STATUS_LABEL] ?? nice(c.status)}
                        </p>
                        <p className="text-caption text-neutral-500">
                          {[c.plant?.name, c.lab?.name ? `Lab: ${c.lab.name}` : 'No lab yet'].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      {canOpenCase && (
                        <button type="button" onClick={() => onOpen(`/ops/cases/${c.company.id}/${c.id}`)}
                          className="text-caption font-semibold text-primary-600 hover:text-primary-700">
                          Open case
                        </button>
                      )}
                    </div>
                    {c.appointment && (
                      <p className="mt-2 text-caption text-neutral-600">
                        Appointment {APPOINTMENT_STATUS_LABEL[c.appointment.status] ?? nice(c.appointment.status)}: {when(c.appointment.scheduledFor)}
                        {c.appointment.location ? ` at ${c.appointment.location.name}${c.appointment.location.city ? `, ${c.appointment.location.city}` : ''}` : ''}
                      </p>
                    )}
                    {c.notifications.length > 0 && (
                      <ul className="mt-2 space-y-0.5 border-t border-neutral-100 pt-2">
                        {c.notifications.map((m, i) => (
                          <li key={i} className="flex flex-wrap gap-x-2 text-caption text-neutral-500">
                            <span>{m.eventType.replace(/[.-]/g, ' ')}</span>
                            <span>· {m.channel === 'IN_APP' ? 'In-app' : nice(m.channel)}</span>
                            <span className={m.status === 'FAILED' ? 'text-danger-600' : m.status === 'SENT' ? 'text-success-600' : ''}>· {nice(m.status)}</span>
                            {m.lastError && m.status !== 'SENT' && <span className="text-danger-600">· {m.lastError}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
        </section>
      </div>
    </Dialog>
  );
}
